import { SummaryStatCard } from 'components/summary-stat-card/summary-stat-card';
import { format0 } from 'utils/number';
import type { WorkspaceSlotsAggregations } from '../types';
import styles from './summary-tiles.module.scss';

interface SummaryTilesProps {
  aggregations: WorkspaceSlotsAggregations | undefined;
}

/**
 * Nine count tiles above the slot comparison table — slot grain and
 * interest (per-UAT) grain, both reflecting the PROMOTED master state. The numbers
 * only move when a promotion happens (ours, or someone else's once pulled
 * in); session confirmations show via the changed-row highlight instead.
 * The red tile — labelled "Needs Further Review" on screen, fed by the
 * `slotsOnHold` aggregation — is an overlay counted INDEPENDENTLY of the three
 * status buckets. A held slot is counted once in whichever bucket its promoted
 * coverage puts it, and once here. A hold downgrades an otherwise Certified
 * slot to In Progress, but a held slot with no promoted coverage stays Not
 * Started, so this tile does NOT imply In Progress. Either way it does not sum
 * with the status buckets to "Total Slots". Reads from the slot-list response's
 * `aggregations` block — no separate fetch. While `aggregations` is
 * `undefined` (initial fetch), every tile shows the `'--'` fallback.
 *
 * @param {SummaryTilesProps} props - Component props.
 * @return {JSX.Element} A nine-tile grid.
 */
export const SummaryTiles = ({
  aggregations,
}: SummaryTilesProps): JSX.Element => (
  <div className={styles.cardsContainer}>
    <SummaryStatCard
      title="Total Slots"
      value={format0(aggregations?.totalSlots)}
    />
    <SummaryStatCard
      title="Certified Slots"
      value={format0(aggregations?.certifiedSlots)}
    />
    <SummaryStatCard
      title="Slots In Progress"
      value={format0(aggregations?.slotsInProgress)}
    />
    <SummaryStatCard
      title="Slots Not Started"
      value={format0(aggregations?.slotsNotStarted)}
    />
    <SummaryStatCard
      title="Needs Further Review"
      value={format0(aggregations?.slotsOnHold)}
      valueColor="red"
    />
    <SummaryStatCard
      title="Total Interests"
      value={format0(aggregations?.totalInterests)}
    />
    <SummaryStatCard
      title="Certified Interests"
      value={format0(aggregations?.certifiedInterests)}
    />
    <SummaryStatCard
      title="Interests In Progress"
      value={format0(aggregations?.interestsInProgress)}
    />
    <SummaryStatCard
      title="Interests Not Started"
      value={format0(aggregations?.interestsNotStarted)}
    />
  </div>
);
